import React from 'react';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import { useTranslation } from 'react-i18next';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import AlertMessage from '@src/new-components/AlertMessage';
import Tooltip from '@src/components/Tooltip';
import {
  STEP_VOICE_DETAIL,
  SUB_STATUS,
  VOICE_CAN_USE,
  VOICE_CLONE_TYPE,
  VOICE_STATUS,
} from '@src/constants/voiceCloning';

import ScheduleSendRounded from '@src/assets/images/schedule-send-rounded.png';
import CancelRequest from '@src/assets/icons/cancel-request.svg';
import SendRounded from '@src/assets/icons/send-rounded.svg';
import CommonButton from '@src/new-components/Button';
import {
  StyledButtonPublish,
  StyledStatusDetailPublishVoice,
  StyledStatusDetails,
} from './index.style';
import WaitingForRemovalTag from './WaitingForRemovalTag';

const StatusDetails = () => {
  const title = 'shareAndEarn';
  const description = 'descriptionShareAndEarn';

  return {
    title,
    description,
  };
};

export const StatusDetail = ({ subStatus }) => {
  const { t } = useTranslation();

  const statusText = {
    [SUB_STATUS.WAITING_FOR_APPROVAL]: 'waitingForApproval',
    [SUB_STATUS.PUBLIC]: 'public',
    [SUB_STATUS.REJECTED]: 'requestRejected',
    // TODO: add text for waiting for community removal
    [SUB_STATUS.WAITING_FOR_REMOVAL]: 'waitingForRemoval',
  };
  const statusBgColor = {
    [SUB_STATUS.WAITING_FOR_APPROVAL]: COLOR.backgroundPending,
    [SUB_STATUS.PUBLIC]: COLOR.backgroundApproved,
    [SUB_STATUS.REJECTED]: COLOR.backgroundRejected,
    [SUB_STATUS.WAITING_FOR_REMOVAL]: COLOR.backgroundWarning,
  };
  const statusTextColor = {
    [SUB_STATUS.WAITING_FOR_APPROVAL]: COLOR.textPending,
    [SUB_STATUS.PUBLIC]: COLOR.textApproved,
    [SUB_STATUS.REJECTED]: COLOR.textRejected,
    [SUB_STATUS.WAITING_FOR_REMOVAL]: COLOR.textWarning,
  };

  return (
    <StyledStatusDetails statusBgColor={statusBgColor[subStatus]}>
      <Typography
        variant="body-12-medium"
        color={statusTextColor[subStatus]}
        style={{ color: statusTextColor[subStatus] }}
      >
        {t(statusText[subStatus])}
      </Typography>
    </StyledStatusDetails>
  );
};

const ButtonPublish = ({ subStatus, setStepVoiceDetail }) => {
  const { t } = useTranslation();

  const showButtonPublish =
    !subStatus ||
    [SUB_STATUS.REJECTED, SUB_STATUS.WAITING_FOR_REMOVAL].includes(subStatus);

  if (!showButtonPublish) return null;

  return (
    <StyledButtonPublish>
      <CommonButton
        variant="contained"
        textProps={{
          variantText: 'body-14-medium',
          color: COLOR.darkBlue,
        }}
        endIcon={
          <img
            src={ScheduleSendRounded}
            alt="schedule-send-rounded"
            width="24px"
          />
        }
        customStyle={{
          height: '40px',
          padding: '12px 16px',
          borderRadius: '8px',
        }}
        onClick={() =>
          setStepVoiceDetail(STEP_VOICE_DETAIL.FORM_SHARE_AND_EARN)
        }
      >
        {t('sendRequest')}
      </CommonButton>
    </StyledButtonPublish>
  );
};

const ButtonAction = ({
  onClick,
  isLoading,
  label,
  endIcon,
  customStyle,
  customTextStyle,
  disabled,
}) => (
  <StyledButtonPublish>
    <CommonButton
      variant="outlined"
      textProps={{
        variantText: 'body-14-medium',
        color: COLOR.darkBlue,
        ...customTextStyle,
      }}
      endIcon={endIcon}
      customStyle={{
        height: '40px',
        padding: '12px 16px',
        borderRadius: '12px',
        ...customStyle,
      }}
      onClick={() => onClick()}
      disabled={isLoading || disabled}
    >
      {label}
    </CommonButton>
  </StyledButtonPublish>
);

const AlertMessageRetentionDays = ({ retentionDays, status }) => {
  const { t } = useTranslation();

  const showAlertInfoRetentionDays = [VOICE_STATUS.PUBLIC].includes(status);

  if (!showAlertInfoRetentionDays) return null;
  const message = retentionDays === 365 ? 'yearNumber' : 'dayNumber';
  return (
    <AlertMessage
      type="default"
      // message={note}
      textProps={{
        variant: 'body-12-regular',
        color: COLOR.descriptionGray,
      }}
      endIcon={
        <Tooltip title={t('retentionDaysTooltip')} placement="top" arrow>
          <InfoRoundedIcon sx={{ color: COLOR.indigoIcon, fontSize: '16px' }} />
        </Tooltip>
      }
      message={t('waitingTimeAfterStopSharingVoice')}
    >
      <Typography variant="body-14-medium" color={COLOR.descriptionGray}>
        {t(message, { value: 1, days: retentionDays })}
      </Typography>
    </AlertMessage>
  );
};

const AlertMessageError = ({ note, subStatus }) => {
  const showAlertError = note && [SUB_STATUS.REJECTED].includes(subStatus);

  if (!showAlertError) return null;

  return (
    <AlertMessage
      type="error"
      message={note}
      textProps={{
        variant: 'body-12-regular',
        color: COLOR.descriptionGray,
      }}
      startIcon={
        <InfoRoundedIcon sx={{ color: COLOR.textRejected, fontSize: '16px' }} />
      }
    />
  );
};

const StatusDetailPublishVoice = ({
  voiceInfo,
  setStepVoiceDetail,
  onCancelPublishVoice,
  isLoadingCancelRequest,
  openPopupUnpublishVoice,
  isLoadingUnpublishVoice,
  onCancelUnpublishVoice,
  isLoadingCancelUnpublishVoice,
}) => {
  const { t } = useTranslation();
  const { title, description } = StatusDetails() || {};

  const showDetailsStatus =
    VOICE_CAN_USE.includes(voiceInfo?.status) &&
    voiceInfo?.type !== VOICE_CLONE_TYPE.ZERO_SHOT;

  const canCancelPublish =
    voiceInfo?.status === VOICE_STATUS.PRIVATE &&
    voiceInfo?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL;

  const showButtonDiscardVoice =
    voiceInfo?.status === VOICE_STATUS.PUBLIC &&
    [
      SUB_STATUS.REJECTED,
      SUB_STATUS.EMPTY,
      SUB_STATUS.WAITING_FOR_APPROVAL,
    ].includes(voiceInfo?.subStatus);

  const disabledDiscardVoice =
    voiceInfo?.status === VOICE_STATUS.PUBLIC &&
    voiceInfo?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL;

  const showButtonCancelDiscardVoice =
    voiceInfo?.status === VOICE_STATUS.PUBLIC &&
    voiceInfo?.subStatus === SUB_STATUS.WAITING_FOR_REMOVAL;

  if (!showDetailsStatus) return null;

  return (
    <StyledStatusDetailPublishVoice>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div>
          <div className="title-wrapper">
            <Typography variant="body-14-medium" color={COLOR.darkGray}>
              {t(title)}
            </Typography>
            {voiceInfo?.status !== VOICE_STATUS.PUBLIC && (
              <StatusDetail subStatus={voiceInfo?.subStatus} />
            )}
            <WaitingForRemovalTag
              subStatus={voiceInfo?.subStatus}
              discardAt={voiceInfo?.discardAt}
            />
          </div>

          <div>
            <Typography
              variant="body-12-regular"
              color={COLOR.descriptionVoiceStatus}
            >
              {t(description)}
            </Typography>
          </div>
        </div>
        {voiceInfo?.status !== VOICE_STATUS.PUBLIC && (
          <ButtonPublish
            setStepVoiceDetail={setStepVoiceDetail}
            subStatus={voiceInfo?.subStatus}
          />
        )}
        {canCancelPublish && (
          <ButtonAction
            onClick={onCancelPublishVoice}
            isLoading={isLoadingCancelRequest}
            label={t('cancelRequest')}
            endIcon={
              <img src={CancelRequest} alt="cancel-request" width="24px" />
            }
          />
        )}
        {showButtonDiscardVoice && (
          <Tooltip
            title={t('cannotUnpublishVoiceUnderReview')}
            placement="top"
            disableHoverListener={!disabledDiscardVoice}
            arrow
            maxWidth="160px"
          >
            <span>
              <ButtonAction
                disabled={disabledDiscardVoice}
                onClick={openPopupUnpublishVoice}
                isLoading={isLoadingUnpublishVoice}
                label={t('unpublishVoice')}
                endIcon={
                  <img src={CancelRequest} alt="cancel-request" width="24px" />
                }
              />
            </span>
          </Tooltip>
        )}
        {showButtonCancelDiscardVoice && (
          <ButtonAction
            onClick={onCancelUnpublishVoice}
            isLoading={isLoadingCancelUnpublishVoice}
            label={t('cancelRequestUnpublishVoice')}
            endIcon={<img src={SendRounded} alt="send-rounded" width="24px" />}
            customStyle={{
              backgroundColor: COLOR.bluev2[8],
              border: 'none',
              '&:hover': {
                cursor: 'pointer',
                backgroundColor: COLOR.bluev2[8],
                border: 'none',
              },
            }}
            customTextStyle={{
              color: COLOR.blueV2,
            }}
          />
        )}
      </div>
      {voiceInfo?.status !== VOICE_STATUS.PUBLIC && (
        <AlertMessageError
          note={voiceInfo?.note}
          subStatus={voiceInfo?.subStatus}
        />
      )}
      <AlertMessageRetentionDays
        retentionDays={voiceInfo?.retentionDays}
        subStatus={voiceInfo?.subStatus}
        status={voiceInfo?.status}
      />
    </StyledStatusDetailPublishVoice>
  );
};

export default StatusDetailPublishVoice;
